import React, { useContext, useEffect, useState } from "react";
import UserContext from "../assets/user_context";
import { NavLink } from "react-router-dom";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { styled, useTheme } from "@mui/material/styles";
import Logo from "../assets/logos/T3-logo-darkMode.svg";
import SmallLogo from "../assets/logos/T3-logo-square.svg";
import SignOutModal from "./signOut";
import ParentNavLink from "./parent_nav_link_themed";
import { StorefrontOutlined, SportsEsports, KeyboardArrowDown } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { useAuth } from "../services/use-auth";
import { useAdminPermissions } from "../utilities/adminCheck";
import * as ROUTES from "../constants/routes";

const StyledLogo = styled("img")(({ theme, collapsed }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  marginBottom: theme.spacing(5),
  width: collapsed ? "48px" : "140px",
  marginLeft: collapsed ? "5px" : "20px",
  marginTop: theme.spacing(2),
  transition: "all 0.3s ease",
}));

const LogoLink = styled(NavLink)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.text.navigation,
  "&:hover": {
    textDecoration: "none",
    color: theme.palette.text.navigation,
  },
}));

const ButtonDiv = styled("div")(({ theme, collapsed }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: collapsed ? "0 3px" : theme.spacing(1),
  marginBottom: theme.spacing(4),
  cursor: "pointer",
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
}));

const LogoutText = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  marginLeft: theme.spacing(1),
  fontSize: "1.2rem",
  fontFamily: theme.typography.subtitle1.fontFamily,
  color: theme.palette.text.navigation,
}));

const SideNavWrapper = styled("div")(({ theme, collapsed }) => ({
  position: "sticky",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.navigation,
  borderRight: `1px solid rgba(255, 255, 255, 0.1)`,
  top: 0,
  left: 0,
  height: "100vh",
  width: collapsed ? "85px" : "220px",
  transition: "width 0.3s ease",
  zIndex: 1100,
  boxShadow: theme.shadows[4],
}));

const StyledSpacerDiv = styled("div")(({ theme }) => ({
  display: "inline-block",
  marginLeft: theme.spacing(1),
}));

const ExpandButton = styled("div")(({ theme, collapsed }) => ({
  position: "absolute",
  right: 0,
  top: "7%",
  transform: collapsed ? "translateX(100%)" : "translateX(0)",
  // Scales gently with viewport: ~20px wide / 40px tall on small screens,
  // up to ~26px / 52px on large screens.
  width: "clamp(20px, 1.5vw, 26px)",
  height: "clamp(40px, 4.5vh, 52px)",
  backgroundColor: theme.palette.primary.main,
  cursor: "pointer",
  borderRadius: collapsed ? "0 8px 8px 0" : "8px 0 0 8px",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
  "&:before": {
    content: '""',
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: !collapsed
      ? "translate(-50%, -50%) rotate(45deg)"
      : "translate(-80%, -50%) rotate(225deg)",
    width: "8px",
    height: "8px",
    borderLeft: `2px solid ${theme.palette.primary.contrastText}`,
    borderBottom: `2px solid ${theme.palette.primary.contrastText}`,
    transition: "transform 0.3s ease-in-out",
  },
}));

const OneCharTitle = styled("p")(({ theme }) => ({
  fontSize: "1.6rem",
  fontFamily: theme.typography.h2.fontFamily,
  textAlign: "center",
  margin: 0,
  color: theme.palette.text.navigation,
}));

const LogoutIconThemed = styled(LogoutOutlinedIcon)(({ theme }) => ({
  color: theme.palette.text.navigation,
}));

const CorporateIconThemed = styled(StorefrontOutlined)(({ theme }) => ({
  color: theme.palette.text.navigation,
}));

const GameControllerIcon = styled(SportsEsports)(({ theme }) => ({
  color: theme.palette.warning.main,
  fontSize: "2rem",
}));

const DesktopNav = ({ collapsed, setCollapsed }) => {
  const theme = useTheme();
  const {
    user,
    userData,
    orgData,
    setSelectOrgOpen,
    setIsChildDataLoaded,
    controlSessions,
  } = useContext(UserContext);
  const { db } = useAuth();
  const { isAdmin: isSystemAdmin } = useAdminPermissions(user?.uid, db);
  const [open, setOpen] = useState(false);

  // Check if currently controlling any org
  const isControlling = controlSessions && controlSessions.length > 0;

  const leaveOrg = async () => {
    // Block org switching if controlling
    if (isControlling) {
      alert(
        "You cannot switch organizations while controlling an org. Please release control first using the banner at the top.",
      );
      return;
    }

    setSelectOrgOpen(true);
    setIsChildDataLoaded(false);
  };

  const handleProperNameLength = (name) => {
    if (name.length > 10) {
      if (name.includes(" ")) {
        return name
          .split(" ")
          .map((word) => word[0])
          .join("")
          .toUpperCase()
          .substring(0, 4);
      } else {
        return name.substring(0, 10).toUpperCase();
      }
    } else {
      return name.toUpperCase();
    }
  };

  const handleTwoLetterOrg = (name) => {
    if (name.includes(" ")) {
      if (
        name.split(" ")[0].toLowerCase() === "the" ||
        name.split(" ")[0].toLowerCase() === "a" ||
        name.split(" ")[0].toLowerCase() === "an"
      ) {
        return name
          .split(" ")[1]
          .substring(0, 1)
          .toUpperCase()
          .concat(name.split(" ")[1].substring(1, 2).toLowerCase());
      } else {
        return name
          .split(" ")[0]
          .substring(0, 1)
          .toUpperCase()
          .concat(name.split(" ")[0].substring(1, 2).toLowerCase());
      }
    } else {
      return name
        .split(" ")[0]
        .substring(0, 1)
        .toUpperCase()
        .concat(name.split(" ")[0].substring(1, 2).toLowerCase());
    }
  };

  return (
    <SideNavWrapper collapsed={collapsed}>
      <ExpandButton
        collapsed={collapsed}
        onClick={() => setCollapsed(!collapsed)}
      />
      <div>
        <LogoLink to="/">
          <StyledLogo
            src={collapsed ? SmallLogo : Logo}
            alt="logo"
            collapsed={collapsed}
          />
        </LogoLink>
        {user && orgData && (
          <React.Fragment>
            <div data-tour="nav-sidebar">
            <ParentNavLink
              to="/foundation/chart-of-accounts"
              collapsed={collapsed}>
              FOUNDATION
            </ParentNavLink>
            <ParentNavLink to="/upkeep/bank-accounts" collapsed={collapsed}>
              UPKEEP
            </ParentNavLink>
            <ParentNavLink
              to={ROUTES.REPORTS_LIBRARY}
              collapsed={collapsed}
              data-testid="nav-link-reports"
              aria-label="Reports">
              REPORTS
            </ParentNavLink>
            <ParentNavLink to="/contacts/contacts" collapsed={collapsed}>
              CONTACTS
            </ParentNavLink>
            <ParentNavLink to="/history" collapsed={collapsed}>
              HISTORY
            </ParentNavLink>
            {orgData && (userData.superAdmin === true || isSystemAdmin) && (
              <ParentNavLink to={userData.superAdmin ? "/super-admin" : "/admin-t3"} collapsed={collapsed}>
                ADMIN
              </ParentNavLink>
            )}
            </div>
          </React.Fragment>
        )}
        <React.Fragment></React.Fragment>
      </div>
      {user && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: collapsed ? "center" : "flex-start",
          }}>
          <React.Fragment>
            {(userData?.isTtcFirmManager || userData?.isTtcBookkeeper) && (
              <ParentNavLink
                to={userData?.isTtcFirmManager ? "/firm-management" : "/bookkeeper"}
                collapsed={collapsed}>
                BOOKKEEPING
              </ParentNavLink>
            )}
            <ParentNavLink to="/account/user-management" collapsed={collapsed}>
              SETTINGS
            </ParentNavLink>

            {(userData?.orgs?.length > 1 ||
              (userData?.superAdmin && orgData?.orgName)) && (
              <Tooltip
                title={
                  isControlling
                    ? "Release control to switch organizations"
                    : `Change from ${
                        orgData?.orgName ? orgData.orgName : "None"
                      }`
                }>
                <ButtonDiv
                  onClick={() => leaveOrg()}
                  collapsed={collapsed}
                  style={{
                    backgroundColor: isControlling
                      ? "rgba(255, 152, 0, 0.15)"
                      : collapsed
                        ? undefined
                        : "rgba(255, 255, 255, 0.06)",
                    border: isControlling
                      ? `2px solid ${theme.palette.warning.main}`
                      : collapsed
                        ? undefined
                        : "1px solid rgba(255, 255, 255, 0.12)",
                  }}>
                  {collapsed ? (
                    isControlling ? (
                      <GameControllerIcon />
                    ) : (
                      <OneCharTitle>
                        {orgData?.orgName?.length > 1
                          ? handleTwoLetterOrg(orgData.orgName)
                          : "ORG"}
                      </OneCharTitle>
                    )
                  ) : (
                    <CorporateIconThemed fontSize="large" />
                  )}
                  {collapsed ? null : (
                    <>
                      <LogoutText
                        style={{
                          flex: 1,
                          color: isControlling
                            ? theme.palette.warning.main
                            : theme.palette.text.navigation,
                          fontWeight: isControlling ? "bold" : undefined,
                        }}>
                        {isControlling
                          ? "CONTROLLING"
                          : orgData?.orgName?.length > 1
                            ? handleProperNameLength(orgData.orgName)
                            : "ORG"}
                      </LogoutText>
                      <KeyboardArrowDown
                        sx={{
                          color: theme.palette.text.navigation,
                          opacity: 0.5,
                          fontSize: "1.2rem",
                        }}
                      />
                    </>
                  )}
                </ButtonDiv>
              </Tooltip>
            )}
          </React.Fragment>
          <ButtonDiv onClick={() => setOpen(true)}>
            <LogoutIconThemed fontSize="large" />
            <StyledSpacerDiv />
            {collapsed ? null : <LogoutText>LOGOUT</LogoutText>}
          </ButtonDiv>
        </div>
      )}
      <SignOutModal open={open} handleClose={() => setOpen(false)} />
    </SideNavWrapper>
  );
};

export default DesktopNav;
